import {z} from "zod"

export const discountRateSchema = z
	.number()
	.min(0.01, "Discount must be greater than 0")
	.max(99.99, "Discount must be less than 100")
	.refine((val) => {
		const decimalPlaces = val.toString().split(".")[1]?.length || 0
		return decimalPlaces <= 3
	}, "Discount can have at most 3 decimal places")

// V2 Schema - supports multiple payment plan tiers
const paymentPlanTierSchema = z.object({
	id: z.string(),
	discountRate: discountRateSchema,
	maxTimeToPayWeeks: z.number().int().positive("Maximum time to pay must be greater than 0"),
	confirmed: z.boolean(),
})

export const offerFormSchemaV2 = z
	.object({
		startDate: z.string().min(1, "Start date is required"),
		endDate: z.string().min(1, "End date is required"),
		notes: z.string().optional(),
		oneOff: z
			.object({
				enabled: z.boolean(),
				discountRate: discountRateSchema.optional(),
			})
			.optional(),
		paymentPlan: z
			.object({
				enabled: z.boolean(),
				tiers: z.array(paymentPlanTierSchema).optional(),
			})
			.optional(),
	})
	.refine(
		(data) => {
			if (data.startDate && data.endDate) {
				return new Date(data.startDate) < new Date(data.endDate)
			}
			return true
		},
		{
			message: "End date must be after start date",
			path: ["endDate"],
		},
	)
	.refine(
		(data) => {
			const hasOneOff = data.oneOff?.enabled && data.oneOff?.discountRate !== undefined
			const hasPlan = data.paymentPlan?.enabled && (data.paymentPlan?.tiers?.length ?? 0) > 0
			return hasOneOff || hasPlan
		},
		{
			message: "At least one offer type (one-off or payment plan) must be enabled",
			path: ["oneOff"],
		},
	)
	.refine(
		(data) => {
			if (data.paymentPlan?.enabled && data.paymentPlan?.tiers) {
				// Check that discount rates are descending (each tier must have lower discount than previous)
				for (let i = 1; i < data.paymentPlan.tiers.length; i++) {
					if (data.paymentPlan.tiers[i].discountRate >= data.paymentPlan.tiers[i - 1].discountRate) {
						return false
					}
				}
			}
			return true
		},
		{
			message: "Each payment plan tier must have a lower discount rate than the previous tier",
			path: ["paymentPlan", "tiers"],
		},
	)

// Original V1 Schema
export const offerFormSchema = z
	.object({
		startDate: z.string().min(1, "Start date is required"),
		endDate: z.string().min(1, "End date is required"),
		notes: z.string().optional(),
		oneOff: z
			.object({
				discountRate: discountRateSchema.optional(),
			})
			.optional(),
		plan: z
			.object({
				discountRate: discountRateSchema.optional(),
				maxTimeToPayWeeks: z.number().int().positive("Maximum time to pay must be greater than 0").optional(),
			})
			.optional(),
	})
	.refine(
		(data) => {
			if (data.startDate && data.endDate) {
				return new Date(data.startDate) < new Date(data.endDate)
			}
			return true
		},
		{
			message: "End date must be after start date",
			path: ["endDate"],
		},
	)
	.refine(
		(data) => {
			const hasOneOff = data.oneOff?.discountRate !== undefined
			const hasPlan = data.plan?.discountRate !== undefined && data.plan?.maxTimeToPayWeeks !== undefined
			return hasOneOff || hasPlan
		},
		{
			message: "At least one offer type (one-off or plan) must be filled",
			path: ["oneOff"],
		},
	)
	.refine(
		(data) => {
			if (data.plan?.discountRate !== undefined && data.plan?.maxTimeToPayWeeks === undefined) {
				return false
			}
			return true
		},
		{
			message: "Maximum time to pay is required when plan discount is specified",
			path: ["plan", "maxTimeToPayWeeks"],
		},
	)

export type OfferFormData = z.infer<typeof offerFormSchema>
export type OfferFormDataV2 = z.infer<typeof offerFormSchemaV2>
export type PaymentPlanTier = z.infer<typeof paymentPlanTierSchema>
